import { Button, Icon } from "components";
import { Empty } from "components/atoms/Empty";
import Skeleton from "components/atoms/layout/Skeleton/Skeleton";
import { SocialNetwork } from "components/atoms/SocialNetwork";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAppDispatch } from "store/hooks";
import { getScrapedComments } from "store/slices/socialScraping.slice";
import { SocialScrapingContent } from "types/scraping/socialScraping.type";
import { ScrapedComment } from "types/scraping/socialScrapingComments.type";
import { showDate } from "utils/workWithData";

export const SocialListItem = (
  props: Partial<SocialScrapingContent> & { isLoading?: boolean }
) => {
  const {
    id,
    platform,
    url,
    postedAt,
    title,
    username,
    content,
    keywords,
    isLoading,
  } = props;
  const [showComment, setShowComment] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [isOverflow, setIsOverflow] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const refContainer = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = refContainer.current;
    if (element) {
      setIsOverflow(element.scrollHeight > 118);
    }
  }, [content, showMore]);
  function formatTextToParagraphs(text: string) {
    if (!text) return [];
    const paragraphs = text
      .replace("Read more", "")
      .split(/(?<=\.)\s+(?=[A-Z])/g)
      .map((p) => p.trim())
      .filter(Boolean);
    return paragraphs;
  }
  if (isLoading) {
    return (
      <>
        {" "}
        {[...Array(10)].map((_, index) => {
          return (
            <div
              key={index}
              className="flex p-6 bg-transparent items-start justify-start gap-4 flex-col w-full h-full border-gray-700"
            >
              <div className="flex h-full items-center justify-center text-white-500 flex-row gap-4">
                <Skeleton width={40} height={40} />
                <div className="flex gap-1 items-center">
                  <Skeleton width={80} height={16} />
                  <Icon name="dot" className="text-gray-700" />
                  <Skeleton width={50} height={14} />
                </div>
              </div>
              <div className="flex flex-col w-full items-start gap-4 justify-between">
                <div className="flex flex-col w-full">
                  <Skeleton width="400px" height={24} />
                  <div className="flex flex-col mt-4">
                    <Skeleton width="550px" height={15} />
                    <Skeleton width="350px" height={15} />
                  </div>
                </div>
              </div>
              <div className="flex flex-row justify-between items-center w-full mt-4">
                <div className="flex flex-row gap-4">
                  <Skeleton width={80} height={24} />
                  <Skeleton width={80} height={24} />
                </div>
                <Skeleton width={100} height={24} />
              </div>
            </div>
          );
        })}
      </>
    );
  }
  //TODO poderia separar isso em outro component
  return (
    <div className="flex felx-row h-fit w-full gap-2 mb-6">
      <div
        className={`flex bg-transparent items-start self-center justify-start gap-4 flex-col rounded-lg ${
          !showComment
            ? "border border-t-0 border-r-0 border-l-0 rounded-none"
            : "border border-t-0 rounded-tr-none border-l"
        }  border-gray-700 w-full h-fit max-h-fit`}
      >
        <div className="flex h-full border-b border-gray-700 w-full">
          <div className="min-h-full w-1 bg-primary-500 rounded-full"></div>
          <div className="flex flex-col w-full h-full">
            <div className="flex h-fit text-white-500 flex-row gap-4 w-full p-4 pt-0">
              <div className="p-2 bg-primary-700 text-primary-300 border border-primary-300 rounded-full">
                <SocialNetwork social={platform!} />
              </div>
              <div className="flex gap-1 flex-row justify-between items-center">
                <div className="text-sm font-bold w-full overflow-hidden">
                  {username}
                </div>
                <div className="text-sm text-gray-400 flex whitespace-nowrap">
                  <Icon name="dot" className="text-gray-600" />
                  {showDate(postedAt)}
                </div>
              </div>
              {url && (
                <Link
                  target="_blank"
                  className="badge sm transition max-h-9 !px-5 ml-auto bg-primary-500  lowercase rounded-xl gap-2 hover:bg-primary-500 hover:ring-gray-800 hover:cursor-pointer qualigence:text-white-500 qualigence:hover:text-white"
                  to={url}
                >
                  <Icon name="share" className="mr-2" size={16} />
                  See post
                </Link>
              )}
            </div>
            <div className="flex flex-col w-full overflow-hidden items-start gap-4 justify-between  px-4 ">
              <h2 className="text-md font-bold max-w-[90%] truncate">
                {title}
              </h2>
              <div
                ref={refContainer}
                className={`${
                  !showMore ? "max-h-[118px] line-clamp-5 " : "max-h-[10000px]"
                } transition-all text-justify duration-100 animation overflow-clip`}
              >
                {formatTextToParagraphs(content as any).map((p, idx) => (
                  <p
                    key={idx}
                    className={`text-gray-300 text-leading ${
                      !showMore && "line-clamp-4"
                    } ${idx > 0 && "mt-1"}`}
                  >
                    {p as string}
                  </p>
                ))}
              </div>
              {isOverflow ? (
                <Button
                  action="button"
                  type="download"
                  className="undeline"
                  onClick={() => setShowMore((e) => !e)}
                >
                  {showMore ? "Hidden" : "Show More"}
                </Button>
              ) : null}
            </div>
            <div className="flex flex-row justify-between items-center w-full py-4 px-4">
              <div className="flex flex-row gap-4">
                <p className="badge sm ring ring-gray-700 bg-gray-700 lowercase rounded-lg  qualigence:text-white-500">
                  #{keywords}
                </p>
              </div>
              <Button
                isProcessing={loadingComments}
                disabled={loadingComments}
                action="button"
                type="primary"
                size="xs"
                className="bg-transparent font-semibold lowercase rounded-full hover:bg-primary-500 max-h-[27.5px] hover:ring-gray-800 hover:cursor-pointer qualigence:text-white-500 qualigence:hover:text-white"
                onClick={() => setShowComment((e) => !e)}
              >
                <Icon name="comment" className="mr-2  " />{" "}
                {showComment ? "Hide Comments" : "Show Comments"}
              </Button>
            </div>
          </div>
        </div>
        {props.id && (
          <Comments
            postId={props.id}
            showComment={showComment}
            handleProcessingComments={setLoadingComments}
            handleHiddeComments={() => setShowComment(false)}
          />
        )}
      </div>
    </div>
  );
};

//TODO poderia separar isso em outro component
const Comments = (props: {
  showComment?: boolean;
  postId: string;
  handleHiddeComments: () => void;
  handleProcessingComments: (isLoading: boolean) => void;
}) => {
  const dispatch = useAppDispatch();
  const [commentDetails, setCommentDetails] = useState<ScrapedComment[] | null>(
    null
  );
  const handleGetCommentDetail = async () => {
    props.handleProcessingComments(true);
    dispatch(
      getScrapedComments({
        postId: props.postId,
      })
    )
      .unwrap()
      .then((res) => {
        setCommentDetails(res.data || null);
      })
      .catch((err) => {
        setCommentDetails([]);
      })
      .finally(() => {
        props.handleProcessingComments(false);
      });
  };
  useEffect(() => {
    if (props.showComment && !commentDetails) {
      handleGetCommentDetail();
    }
  }, [props.postId, props.showComment]);
  return (
    <div
      className={`flex w-full h-fit flex-col ${
        props.showComment && commentDetails ? "flex" : "hidden"
      }`}
    >
      {commentDetails?.length === 0 ? (
        <div className=" p-4 pt-0 flex w-full">
          <Empty
            type="no-messages"
            title="No Comments Found"
            message="No comments yet, try again later"
          />
        </div>
      ) : (
        <>
          <div className="flex gap-4 items-center text-white-500 p-4 pt-0 ">
            <Icon name="comment" size={24} />
            <h2 className="text-lg font-semibold">
              {commentDetails?.length} Comments{" "}
            </h2>
          </div>
          <div className="flex flex-col divide-y divide-gray-700 qualigence:divide-gray-700">
            {commentDetails?.map((item) => {
              return (
                <div
                  key={item.comment}
                  className={`flex relative p-4 flex-col w-full items-start gap-4 justify-between `}
                >
                  <div className="flex flex-col w-full z-10 items-start gap-4 justify-between  -ml-8">
                    <div className="flex items-center w-full gap-4">
                      <div className="p-2 bg-primary-500 rounded-full text-white-500 min-w-8 max-w-8 min-h-8 max-h-8 flex items-center justify-center">
                        {item.username[0].toUpperCase()}
                      </div>
                      <div className="flex flex-row gap-1 items-center w-full">
                        <h2 className="text-md font-bold  truncate">
                          {item.username}
                        </h2>
                        <Icon name="dot" className="text-gray-600" />
                        <small className="text-md truncate text-gray-500 text-xs">
                          {" "}
                          {showDate(item.date_comment)}
                        </small>
                      </div>
                      <div className="flex flex-row gap-4 items-end w-full justify-end ">
                        <p className="badge sm bg-primary-700 text-primary-300  border border-primary-300 lowercase rounded-lg qualigence:text-white-500">
                          <Icon name="like" size={14} className="mr-1" />
                          {item.engagement.likes}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col w-full items-start gap-4 justify-between pl-12">
                      <p className="text-sm text-gray-300 line-clamp-5">
                        {item.comment}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex w-full justify-center items-center my-4">
            <Button
              action="button"
              type="primary"
              size="xs"
              className="undeline"
              onClick={() => props.handleHiddeComments()}
            >
              <Icon name="comment" className="mr-2" /> Hide Comments
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
